// Weekly Management Summary — the "Contract Operational Summary" view:
// branded header grid, 4 coloured tiles (Time/Safety/Information/Risk) and a
// Topic/Summary bullet table, built from the AI-generated (or rule-based
// fallback) content.ai_summary — see services/fortnightly_reports on the
// backend. A nominated reviewer can edit the bullets/ticks in Edit mode
// before finalising (PATCH /fortnightly-reports/:id/ai-summary); any later
// WSR regeneration then leaves the edited summary untouched. The full
// section-by-section WSR rollup (used for drill-down only, never exported)
// stays available below as a collapsed detail panel.
import { useEffect, useState } from "react";
import { ChevronDown, ExternalLink, Loader2, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { PhotoGrid } from "./WsrKit";

const TILE = {
    time: { hex: "#D9A13B", title: "Time", ticks: [["on_plan", "On plan"], ["watch", "Watch"], ["delay", "Delay"]], mode: "single", field: "tick" },
    safety: { hex: "#4CAF50", title: "Safety", ticks: [["no_incidents", "No incidents"], ["actions_open", "Actions open"]], mode: "multi" },
    information: { hex: "#4A7FD1", title: "Information", ticks: [["received", "Received"], ["required", "Required"]], mode: "single", field: "tick" },
    risk: { hex: "#D9534F", title: "Risk", ticks: [["materials", "Materials"], ["labour", "Labour"], ["weather", "Weather"]], mode: "multi" },
};
const TOPIC_ORDER = ["time", "safety", "information", "risk"];

function Tile({ topicKey, data, editMode, onChange }) {
    const meta = TILE[topicKey];
    const toggle = (key) => {
        if (meta.mode === "single") onChange({ ...data, [meta.field]: key });
        else onChange({ ...data, [key]: !data[key] });
    };
    const isOn = (key) => (meta.mode === "single" ? data[meta.field] === key : !!data[key]);
    return (
        <div data-testid={`ms-tile-${topicKey}`} className="rounded-2xl p-4 space-y-2 text-white" style={{ background: meta.hex }}>
            <div className="text-[12px] font-bold uppercase tracking-[.06em]">{meta.title}</div>
            <div className="flex flex-wrap gap-1.5">
                {meta.ticks.map(([key, label]) => (
                    <button key={key} type="button" data-testid={`ms-tile-${topicKey}-${key}`} disabled={!editMode}
                            onClick={() => editMode && toggle(key)}
                            className="px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                            style={{ borderColor: "rgba(255,255,255,.55)", background: isOn(key) ? "rgba(255,255,255,.92)" : "transparent", color: isOn(key) ? meta.hex : "#fff" }}>
                        {isOn(key) ? "☑" : "☐"} {label}
                    </button>
                ))}
            </div>
            {editMode ? (
                <textarea data-testid={`ms-tile-${topicKey}-narrative`} value={data.narrative || ""} rows={2}
                          onChange={(e) => onChange({ ...data, narrative: e.target.value })}
                          placeholder="One-line narrative…"
                          className="w-full rounded-lg bg-white/15 placeholder-white/70 border border-white/30 px-2 py-1 text-[11.5px] text-white focus:outline-none" />
            ) : (
                <p className="text-[11.5px] text-white/90">{data.narrative || "—"}</p>
            )}
        </div>
    );
}

function TopicSummaryRow({ topicKey, data, editMode, onChange }) {
    const meta = TILE[topicKey];
    const bulletsText = (data.bullets || []).join("\n");
    return (
        <div data-testid={`ms-topic-${topicKey}`} className="grid grid-cols-[110px_1fr] gap-3 py-2.5 border-b border-[#F0ECE5] last:border-0">
            <div className="text-[12px] font-bold uppercase tracking-[.04em] pt-0.5" style={{ color: meta.hex }}>
                {topicKey === "risk" ? "Risk / Overview" : meta.title}
            </div>
            {editMode ? (
                <textarea data-testid={`ms-topic-${topicKey}-bullets`} value={bulletsText} rows={3}
                          onChange={(e) => onChange({ ...data, bullets: e.target.value.split("\n") })}
                          placeholder="One bullet per line…"
                          className="w-full rounded-lg border border-[#E5E0DA] px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-[#C75A1B]/25" />
            ) : (
                <ul className="text-[12.5px] text-[#1F1F1F] space-y-0.5 list-disc pl-4">
                    {(data.bullets || []).length ? data.bullets.map((b, i) => <li key={i}>{b}</li>) : <li className="list-none text-[#9A968F]">—</li>}
                </ul>
            )}
        </div>
    );
}

export function ContractOperationalSummary({ report, content, editMode, onUpdated }) {
    const brand = report.client_branding?.primary_colour || "#C75A1B";
    const header = content.header || {};
    const [draft, setDraft] = useState(content.ai_summary || {});
    const [saving, setSaving] = useState(false);

    useEffect(() => { setDraft(content.ai_summary || {}); }, [content.ai_summary]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/fortnightly-reports/${report.id}/ai-summary`, {
                time: draft.time || {}, safety: draft.safety || {}, information: draft.information || {}, risk: draft.risk || {},
            });
            onUpdated(data);
            toast.success("Summary updated");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <section data-testid="ms-contract-operational-summary" className="bg-white rounded-2xl border border-[#E5E0DA] overflow-hidden shadow-brand-sm">
            <div className="px-5 py-3 text-white font-bold text-[13px] tracking-[.03em] uppercase" style={{ background: brand }}>
                Contract Report Overview
            </div>
            <div className="p-5 sm:p-6 space-y-5">
                <div className="grid sm:grid-cols-3 gap-x-6 gap-y-3 rounded-xl border border-[#E5E0DA] bg-[#F6F4F0] p-4">
                    {[
                        ["Contract", header.project_name], ["Site Manager", header.site_manager_name], ["Report Number", report.report_number],
                        ["Client / Employer", header.client_employer], ["Client / Consultant", header.client_rep], ["Distribution", header.distribution],
                    ].map(([label, value]) => (
                        <div key={label}>
                            <div className="text-[10.5px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">{label}</div>
                            <div className="text-[13px] font-semibold text-[#1F1F1F]">{value || "—"}</div>
                        </div>
                    ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    {TOPIC_ORDER.map((k) => (
                        <Tile key={k} topicKey={k} data={draft[k] || {}} editMode={editMode} onChange={(v) => setDraft((d) => ({ ...d, [k]: v }))} />
                    ))}
                </div>

                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[.05em] text-[#8B867E] mb-1">Topic / Summary</div>
                    {TOPIC_ORDER.map((k) => (
                        <TopicSummaryRow key={k} topicKey={k} data={draft[k] || {}} editMode={editMode} onChange={(v) => setDraft((d) => ({ ...d, [k]: v }))} />
                    ))}
                </div>

                {editMode && (
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[11.5px] text-[#9A968F]">
                            {draft.source === "manual" ? `Edited by ${draft.edited_by_name || "a reviewer"}` : draft.source === "ai" ? "AI-drafted from this week's WSR" : "Rule-based draft"}
                        </span>
                        <button data-testid="ms-ai-summary-save" onClick={save} disabled={saving}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212] disabled:opacity-50">
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save summary
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}

const TONE = {
    green: { bg: "#E4F1E6", fg: "#2E7D32" },
    amber: { bg: "#FDF0DC", fg: "#B5720A" },
    red: { bg: "#FBE3E1", fg: "#B3261E" },
    neutral: { bg: "#F5F3F0", fg: "#6F6A64" },
};

function TonePill({ tone, children }) {
    const t = TONE[tone] || TONE.neutral;
    return <span className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold" style={{ background: t.bg, color: t.fg }}>{children}</span>;
}

function MsSection({ n, title, wsrId, hasIssues, noIssuesLabel = "No issues", children }) {
    return (
        <section data-testid={`ms-section-${n || "headline"}`} className="bg-white rounded-2xl border border-[#E5E0DA] p-5 sm:p-6 space-y-3 shadow-brand-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-[15px] font-bold text-[#1F1F1F]">{n && <span className="text-[#C75A1B]">{n}</span>} {title}</h3>
                {wsrId && n && (
                    <Link to={`/app/weekly-reports/${wsrId}#wsr-section-${n}`} data-testid={`ms-section-link-${n}`}
                          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#2A5DB0] hover:underline">
                        View in WSR <ExternalLink size={11} />
                    </Link>
                )}
            </div>
            {hasIssues === false ? (
                <p data-testid={`ms-no-issues-${n || "section"}`} className="text-[13px] font-semibold text-[#2E7D32]">{noIssuesLabel}</p>
            ) : children}
        </section>
    );
}

function KvGrid({ pairs }) {
    return (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {pairs.map(([label, value]) => (
                <div key={label}>
                    <div className="text-[10.5px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">{label}</div>
                    <div className="text-[13px] font-semibold text-[#1F1F1F]">{value || "—"}</div>
                </div>
            ))}
        </div>
    );
}

function MiniTable({ columns, rows, testId }) {
    if (!rows?.length) return null;
    return (
        <div data-testid={testId} className="rounded-xl border border-[#E5E0DA] overflow-hidden bg-white">
            <table className="w-full text-[12.5px]">
                <thead>
                    <tr className="bg-[#F5F3F0] text-left">
                        {columns.map((c) => <th key={c.key} className="px-3 py-2 font-semibold text-[#8B867E] uppercase text-[10.5px] tracking-[.04em]">{c.label}</th>)}
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#F0ECE5]">
                    {rows.map((r, i) => (
                        <tr key={i}>
                            {columns.map((c) => <td key={c.key} className="px-3 py-2 align-top text-[#1F1F1F]">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function FullWsrDetail({ content, wsrId }) {
    const headline = content.headline || {};
    const overview = content.overview || [];
    const es = content.exec_summary || {};
    const programme = content.programme || {};
    const resources = content.resources || {};
    const plant = content.plant || {};
    const quality = content.quality || {};
    const hs = content.health_safety || {};
    const cdm = content.cdm || {};
    const inductions = content.inductions || {};
    const risks = content.risks || {};
    const clientActions = content.client_actions || {};
    const photos = content.photos || [];

    return (
        <div className="space-y-4">
            <MsSection title="Headline">
                <div className="grid sm:grid-cols-2 gap-3">
                    {[["time", "Time"], ["safety", "Safety"], ["information", "Information"], ["risk", "Risk"]].map(([key, label]) => {
                        const h = headline[key] || {};
                        return (
                            <div key={key} data-testid={`ms-headline-${key}`} className="rounded-xl border border-[#E5E0DA] p-3 space-y-1.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-[13px] font-bold text-[#1F1F1F]">{label}</span>
                                    <TonePill tone={{ "On plan": "green", Watch: "amber", Delay: "red", "No incidents": "green", "Actions open": "amber",
                                        Received: "green", Required: "amber", Clear: "green", Flagged: "amber" }[h.status] || "neutral"}>{h.status || "—"}</TonePill>
                                </div>
                                <p className="text-[12.5px] text-[#6F6A64]">{h.summary || "—"}</p>
                            </div>
                        );
                    })}
                </div>
            </MsSection>

            <MsSection n="1.0" title="Weekly Report Overview" wsrId={wsrId}>
                <div className="grid sm:grid-cols-2 gap-3">
                    {overview.map((o) => (
                        <div key={o.key} data-testid={`ms-overview-${o.key}`} className="rounded-xl border border-[#E5E0DA] p-3 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[13px] font-bold text-[#1F1F1F]">{o.label}</span>
                                <TonePill tone={o.tone}>{o.status || "—"}</TonePill>
                            </div>
                            {o.note && <p className="text-[12px] text-[#6F6A64]">{o.note}</p>}
                        </div>
                    ))}
                </div>
            </MsSection>

            <MsSection n="2.0" title="Executive Summary" wsrId={wsrId}>
                {[["Key achievements", es.key_achievements], ["Key concerns / escalations", es.key_concerns], ["Outlook", es.outlook]].map(([lbl, val]) => (
                    <div key={lbl}>
                        <div className="text-[10.5px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">{lbl}</div>
                        <p className="text-[13px] text-[#1F1F1F] whitespace-pre-wrap">{val || "—"}</p>
                    </div>
                ))}
            </MsSection>

            <MsSection n="3.0" title="Programme" wsrId={wsrId} hasIssues={programme.has_issues}>
                <KvGrid pairs={[
                    ["Planned %", programme.planned_pct], ["Actual %", programme.actual_pct],
                    ["Variance", programme.variance != null ? `${programme.variance > 0 ? "+" : ""}${programme.variance} pts` : "—"],
                    ["Delay reason", programme.delay_reason],
                ]} />
                {programme.delay_note && <p className="text-[12.5px] text-[#1F1F1F]"><span className="font-semibold">Root cause:</span> {programme.delay_note}</p>}
                {(programme.days_lost || programme.impact_notes) && (
                    <p className="text-[12.5px] text-[#1F1F1F]"><span className="font-semibold">Days lost:</span> {programme.days_lost || 0} — {programme.impact_notes}</p>
                )}
                <MiniTable testId="ms-programme-recovery" columns={[
                    { key: "ref", label: "Ref" }, { key: "title", label: "Action" }, { key: "owner", label: "Owner" },
                    { key: "due_date", label: "Due" }, { key: "status", label: "Status" },
                ]} rows={programme.recovery_actions} />
                {programme.look_ahead && <p className="text-[12.5px] text-[#1F1F1F]"><span className="font-semibold">Look-ahead:</span> {programme.look_ahead}</p>}
                <div className="grid sm:grid-cols-3 gap-2 pt-1">
                    {["time", "cost", "information"].map((k) => {
                        const r = (programme.rag || {})[k] || {};
                        return (
                            <div key={k} className="rounded-xl border border-[#E5E0DA] p-2.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-[12px] font-semibold capitalize text-[#1F1F1F]">{k}</span>
                                    <TonePill tone={{ Green: "green", Amber: "amber", Red: "red" }[r.rag] || "neutral"}>{r.rag || "—"}</TonePill>
                                </div>
                                {r.note && <p className="text-[11.5px] text-[#6F6A64] mt-1">{r.note}</p>}
                            </div>
                        );
                    })}
                </div>
            </MsSection>

            <MsSection n="4.0" title="Resources On Site" wsrId={wsrId} hasIssues={resources.has_issues}>
                <MiniTable testId="ms-resources-headcount" columns={[
                    { key: "category", label: "Category" }, { key: "count", label: "Avg this week" }, { key: "vs_last_week", label: "vs last week" },
                ]} rows={resources.headcount} />
                <p className="text-[12.5px] text-[#1F1F1F]"><span className="font-semibold">Labour:</span> {resources.labour_status || "Not confirmed"} — {resources.labour_note}</p>
                {resources.flagged_subcontractors?.length > 0 && (
                    <MiniTable testId="ms-resources-flagged-subs" columns={[
                        { key: "subcontractor", label: "Subcontractor" }, { key: "trade", label: "Trade" },
                        { key: "low_scores", label: "Low scores", render: (r) => (r.low_scores || []).join(", ") }, { key: "issues", label: "Issues / action" },
                    ]} rows={resources.flagged_subcontractors} />
                )}
            </MsSection>

            <MsSection n="5.0" title="Plant / Equipment On Site" wsrId={wsrId} hasIssues={plant.has_issues}>
                <p className="text-[12.5px] text-[#1F1F1F]">{plant.count || 0} item(s) on site this week</p>
                <MiniTable testId="ms-plant-flagged" columns={[
                    { key: "item", label: "Item" }, { key: "status", label: "Status" }, { key: "loler_status", label: "LOLER" },
                    { key: "puwer_status", label: "PUWER" }, { key: "action_taken", label: "Action taken" },
                ]} rows={plant.flagged} />
            </MsSection>

            <MsSection n="6.0" title="Quality Inspections" wsrId={wsrId} hasIssues={quality.has_issues}>
                <p className="text-[12.5px] text-[#1F1F1F]">
                    {quality.inspections_completed || 0} inspection(s) completed
                    {quality.ncr_raised && " · NCR raised this week"}
                    {quality.open_snags && " · open snags/defects"}
                </p>
                <MiniTable testId="ms-quality-flagged-inspections" columns={[
                    { key: "date", label: "Date" }, { key: "inspection", label: "Inspection" }, { key: "itp_stage", label: "ITP stage" }, { key: "result", label: "Result" },
                ]} rows={quality.flagged_inspections} />
                <MiniTable testId="ms-quality-flagged-checklist" columns={[{ key: "item", label: "Checklist item (No)" }, { key: "note", label: "Note" }]} rows={quality.flagged_checklist} />
            </MsSection>

            <MsSection n="7.0" title="Health & Safety Inspections" wsrId={wsrId} hasIssues={hs.has_issues}>
                <p className="text-[12.5px] text-[#1F1F1F]">{hs.inspections_completed || 0} inspection(s) completed</p>
                <MiniTable testId="ms-hs-incidents" columns={[
                    { key: "date", label: "Date" }, { key: "description", label: "Incident / near miss" }, { key: "status", label: "Status" }, { key: "result", label: "Result" },
                ]} rows={hs.incidents} />
                <MiniTable testId="ms-hs-flagged-checklist" columns={[{ key: "item", label: "Checklist item (No)" }, { key: "note", label: "Note" }]} rows={hs.flagged_checklist} />
            </MsSection>

            <MsSection n="8.0" title="CDM Compliance Checklist" wsrId={wsrId} hasIssues={cdm.has_issues}>
                <MiniTable testId="ms-cdm-duty-no" columns={[{ key: "item", label: "Duty answered No" }, { key: "note", label: "Note" }]} rows={cdm.duty_no} />
                <MiniTable testId="ms-cdm-site-control" columns={[
                    { key: "area", label: "Site control area" }, { key: "position", label: "Position" }, { key: "action_owner", label: "Action / owner" },
                ]} rows={cdm.site_control_flags} />
                <MiniTable testId="ms-cdm-critical" columns={[{ key: "action", label: "Pending critical action" }, { key: "owner_due", label: "Owner / due" }]} rows={cdm.pending_critical} />
            </MsSection>

            <MsSection n="9.0" title="Site Inductions & Toolbox Talks" wsrId={wsrId} hasIssues={inductions.has_issues}
                       noIssuesLabel="No expired cards or repeat offenders this week">
                <p className="text-[12.5px] text-[#1F1F1F]">{inductions.summary_line} · {inductions.toolbox_count || 0} toolbox talk(s) delivered</p>
                {inductions.expired_cards && <p className="text-[12.5px] text-[#B3261E]"><span className="font-semibold">Expired cards:</span> {inductions.expired_cards_detail}</p>}
                {inductions.repeat_offenders && <p className="text-[12.5px] text-[#B3261E]"><span className="font-semibold">Repeat offenders:</span> {inductions.repeat_offenders_detail}</p>}
            </MsSection>

            <MsSection n="10.0" title="Risks & Issues" wsrId={wsrId} hasIssues={risks.has_issues}>
                <MiniTable testId="ms-risks-open" columns={[
                    { key: "ref", label: "Ref" }, { key: "risk", label: "Risk" }, { key: "impact", label: "Impact" }, { key: "owner", label: "Owner" }, { key: "age_days", label: "Age (days)" },
                ]} rows={risks.open} />
                {risks.awaiting_sign_off?.length > 0 && (
                    <>
                        <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] pt-1">Awaiting sign-off</div>
                        <MiniTable testId="ms-risks-awaiting" columns={[
                            { key: "ref", label: "Ref" }, { key: "risk", label: "Risk" }, { key: "impact", label: "Impact" }, { key: "owner", label: "Owner" }, { key: "age_days", label: "Age (days)" },
                        ]} rows={risks.awaiting_sign_off} />
                    </>
                )}
            </MsSection>

            <MsSection n="11.0" title="Client Actions & Instructions Required" wsrId={wsrId} hasIssues={clientActions.has_issues}>
                <MiniTable testId="ms-client-actions" columns={[
                    { key: "ref", label: "Ref" }, { key: "action", label: "Action" }, { key: "due", label: "Due" },
                    { key: "status", label: "Status", render: (r) => (r.overdue ? <span className="text-[#B3261E] font-semibold">OVERDUE · {r.status}</span> : r.status) },
                ]} rows={clientActions.outstanding} />
            </MsSection>

            <MsSection n="12.0" title="Supporting Information" wsrId={wsrId}>
                {photos.length > 0 && wsrId ? (
                    <PhotoGrid photos={photos} apiBase={API} reportId={wsrId} disabled uploading={false} onCaption={() => {}} onDelete={() => {}} onUpload={() => {}} />
                ) : (
                    <p className="text-[13px] text-[#8B867E] italic">No photos attached this week.</p>
                )}
            </MsSection>
        </div>
    );
}

export default function WeeklyManagementSummaryView({ report, content, editMode, onUpdated }) {
    const wsrId = report.source_report_id;
    const [showDetail, setShowDetail] = useState(false);

    return (
        <div data-testid="ms-weekly-summary-view" className="space-y-4">
            <ContractOperationalSummary report={report} content={content} editMode={editMode} onUpdated={onUpdated} />

            <button data-testid="ms-toggle-full-wsr-detail" onClick={() => setShowDetail((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F]">
                <ChevronDown size={14} className={showDetail ? "rotate-180 transition-transform" : "transition-transform"} />
                {showDetail ? "Hide" : "Show"} full WSR section detail
                {wsrId && <span className="text-[#9A968F] font-normal ml-1">(drill-down only — not part of the exported pack)</span>}
            </button>

            {showDetail && <FullWsrDetail content={content} wsrId={wsrId} />}
        </div>
    );
}
